/**
 * FaqSettings — Q&A pairs the AI consults when replying to a lead.
 *
 * Maps to GET / PUT /api/settings/faq. The AI worker reads from the same
 * user_settings row (key 'faq_entries') and injects matching entries into
 * the reply prompt's system message when a lead's inbound message looks
 * like a question. First DMs and follow-ups never see this — only replies.
 *
 * UX:
 *   - Empty state with a single "Add your first Q&A" call-to-action.
 *   - Each entry is an inline card with question input + answer textarea +
 *     remove button. Order is presentational only (the AI is told to match
 *     by similarity, not position).
 *   - Save is explicit (one button at the bottom) — pending edits stay
 *     local until clicked. Dirty state surfaces a "You have unsaved
 *     changes" hint so users don't navigate away expecting auto-save.
 *   - 4000-char limit per answer matches the worker validator.
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  CheckCircle2,
  HelpCircle,
  Plus,
  Trash2,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useApi } from '@/hooks/use-api'
import type { FaqConfig, FaqEntry } from '@dealcloser/shared'
import { DEFAULT_FAQ } from '@dealcloser/shared'

const FAQ_QUERY_KEY = ['settings', 'faq'] as const

const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px] resize-y'

const MAX_QUESTION_LEN = 500
const MAX_ANSWER_LEN = 4000
const MAX_ENTRIES = 100

function newEntryId(): string {
  return `faq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export default function FaqSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [entries, setEntries] = useState<FaqEntry[]>([])
  const [savedSuccess, setSavedSuccess] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: FAQ_QUERY_KEY,
    queryFn: async (): Promise<FaqConfig> => {
      const res = await apiFetch<FaqConfig>('/api/settings/faq')
      if (!res.success || !res.data) return DEFAULT_FAQ
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  // Pull server state into local form state on first load (and on refetch).
  useEffect(() => {
    if (data) {
      setEntries(
        data.entries.map((e) => ({
          id: e.id,
          question: e.question,
          answer: e.answer,
        })),
      )
    }
  }, [data])

  // Dirty-state detection: compare the form against the last server snapshot.
  // We compare normalized JSON so whitespace-only edits still register as dirty.
  const isDirty = useMemo(() => {
    if (!data) return false
    return JSON.stringify(entries) !== JSON.stringify(data.entries)
  }, [data, entries])

  const saveMutation = useMutation({
    mutationFn: async (next: readonly FaqEntry[]): Promise<void> => {
      // Strip empty entries before sending — keeps the saved state tidy and
      // matches the worker's "non-empty question + answer" validation. The
      // user can have an empty card in the UI while drafting, but we never
      // persist it.
      const cleaned = next
        .map((e) => ({
          id: e.id,
          question: e.question.trim(),
          answer: e.answer.trim(),
        }))
        .filter((e) => e.question.length > 0 && e.answer.length > 0)

      const res = await apiFetch<FaqConfig>('/api/settings/faq', {
        method: 'PUT',
        body: { entries: cleaned },
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAQ_QUERY_KEY })
      setSavedSuccess(true)
      setValidationError(null)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  function handleAdd(): void {
    if (entries.length >= MAX_ENTRIES) {
      setValidationError(`You've reached the ${MAX_ENTRIES}-entry maximum. Remove an entry to add another.`)
      return
    }
    setValidationError(null)
    setEntries((prev) => [...prev, { id: newEntryId(), question: '', answer: '' }])
  }

  function handleUpdate(id: string, field: 'question' | 'answer', value: string): void {
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    )
  }

  function handleDelete(id: string): void {
    setEntries((prev) => prev.filter((e) => e.id !== id))
  }

  function handleSave(): void {
    // Client-side preview of what the worker will reject. We don't block
    // saving entirely on "one empty card while drafting" — those just get
    // stripped — but we do flag malformed entries the user clearly intends
    // to ship (e.g., a question with no answer, or an answer over the cap).
    setValidationError(null)
    for (const e of entries) {
      const q = e.question.trim()
      const a = e.answer.trim()
      if (q.length > 0 && a.length === 0) {
        setValidationError('One of your entries has a question but no answer. Fill it in or remove the entry.')
        return
      }
      if (q.length === 0 && a.length > 0) {
        setValidationError('One of your entries has an answer but no question. Fill it in or remove the entry.')
        return
      }
      if (e.question.length > MAX_QUESTION_LEN) {
        setValidationError(`A question exceeds the ${MAX_QUESTION_LEN}-character limit. Shorten it before saving.`)
        return
      }
      if (e.answer.length > MAX_ANSWER_LEN) {
        setValidationError(`An answer exceeds the ${MAX_ANSWER_LEN}-character limit. Shorten it before saving.`)
        return
      }
    }
    saveMutation.mutate(entries)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
          FAQ — answers the AI uses on replies
        </CardTitle>
        <CardDescription>
          When a lead replies and asks a question, the AI checks this list first. If their
          question matches one of yours, the AI uses your answer as the source of truth — so
          things like pricing, integrations, or common objections are answered the way you'd
          answer them. <strong>Only used on replies</strong> — first DMs and follow-ups stay
          focused on the offer.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading FAQ…
          </div>
        ) : entries.length === 0 ? (
          <EmptyState onAdd={handleAdd} />
        ) : (
          <ul className="flex flex-col gap-3">
            {entries.map((entry, idx) => (
              <li key={entry.id}>
                <EntryCard
                  index={idx}
                  entry={entry}
                  disabled={saveMutation.isPending}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              </li>
            ))}
          </ul>
        )}

        {entries.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={saveMutation.isPending || entries.length >= MAX_ENTRIES}
            className="self-start"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add another Q&amp;A
          </Button>
        )}

        {validationError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        {saveMutation.error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save'}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            {isDirty ? (
              <span className="text-amber-400">You have unsaved changes.</span>
            ) : entries.length > 0 ? (
              <>
                {entries.filter((e) => e.question.trim() && e.answer.trim()).length} active entr
                {entries.filter((e) => e.question.trim() && e.answer.trim()).length === 1 ? 'y' : 'ies'}
              </>
            ) : (
              'No FAQ entries yet.'
            )}
          </p>
          <div className="flex items-center gap-2">
            {savedSuccess && (
              <span className="flex items-center gap-1 text-xs text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            <Button onClick={handleSave} disabled={saveMutation.isPending || !isDirty} size="sm">
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save FAQ'
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function EmptyState({ onAdd }: { readonly onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border bg-[rgba(255,255,255,0.02)] py-8 text-center">
      <HelpCircle className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium">No FAQ entries yet</p>
      <p className="max-w-md text-xs text-muted-foreground">
        Add the questions leads typically ask — pricing, integrations, "how does this differ
        from X", "is this LinkedIn-compliant" — and the AI will use your answers on replies.
      </p>
      <Button size="sm" onClick={onAdd}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        Add your first Q&amp;A
      </Button>
    </div>
  )
}

function EntryCard({
  index,
  entry,
  disabled,
  onUpdate,
  onDelete,
}: {
  readonly index: number
  readonly entry: FaqEntry
  readonly disabled: boolean
  readonly onUpdate: (id: string, field: 'question' | 'answer', value: string) => void
  readonly onDelete: (id: string) => void
}) {
  const questionOverLimit = entry.question.length > MAX_QUESTION_LEN
  const answerOverLimit = entry.answer.length > MAX_ANSWER_LEN
  return (
    <div className="flex flex-col gap-3 rounded-md border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="rounded bg-[rgba(28,63,255,0.12)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-cobalt-bright)]">
          Q{index + 1}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(entry.id)}
          disabled={disabled}
          aria-label={`Delete Q${index + 1}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`faq-q-${entry.id}`} className="text-xs">Question the lead might ask</Label>
        <Input
          id={`faq-q-${entry.id}`}
          value={entry.question}
          onChange={(e) => onUpdate(entry.id, 'question', e.target.value)}
          placeholder='e.g. "How much does this cost?"'
          maxLength={MAX_QUESTION_LEN + 100}
          disabled={disabled}
          aria-invalid={questionOverLimit}
        />
        <div className="flex items-center justify-between">
          <span className={`text-[10px] ${questionOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
            {entry.question.length}/{MAX_QUESTION_LEN}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`faq-a-${entry.id}`} className="text-xs">How the AI should answer</Label>
        <textarea
          id={`faq-a-${entry.id}`}
          className={TEXTAREA_CLASS}
          value={entry.answer}
          onChange={(e) => onUpdate(entry.id, 'answer', e.target.value)}
          placeholder='e.g. "We offer two plans: Basic at $29/mo and Pro at $69/mo. Both include AI-written LinkedIn outreach and reply handling."'
          maxLength={MAX_ANSWER_LEN + 200}
          disabled={disabled}
          aria-invalid={answerOverLimit}
        />
        <div className="flex items-center justify-between">
          <span className={`text-[10px] ${answerOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
            {entry.answer.length}/{MAX_ANSWER_LEN}
          </span>
        </div>
      </div>
    </div>
  )
}
